<template>
  <div class="zrsy2-page">
    <!-- 顶栏常驻，**放在状态分支外面**：加载中 / 未审核 / 码不存在这几个状态同样要有
         平台名与语言切换，否则英文用户在这些状态下只能看到一句中文提示。 -->
    <header class="topbar">
      <span class="brand">{{ t('platform') }}</span>
      <button class="lang" type="button" @click="switchLang">{{ lang === 'zh' ? '中文' : 'EN' }}</button>
    </header>

    <div v-if="loading" class="hint">{{ t('hintLoading') }}</div>

    <!-- 未审核 / 码不存在 / 其它异常，都只给一句话，不暴露内部状态 -->
    <div v-else-if="state !== 'ok'" class="hint">{{ hintText }}</div>

    <template v-else>
      <!-- 商品主图：**左右轮播，不自动播放**〔2026-09-14 晚 用户定〕。
           ⚠️ 这一处 2026-09-14 改过两次：先是 van-swipe 轮播 → 用户说「滚动本身也不需要」
           改成竖排平铺 → 同日又定回轮播。**别按旧记录改回竖排**，两次决定都记在
           zhongren/doc/plan.md 里。
           ⚠️ autoplay 不传 = 不自动轮播（vant 2 的默认值就是 0），要手动左右滑。
           thumb() 走 OSS 图片处理压到 750px 宽；点开预览时取的是原图。 -->
      <!-- ⚠️ 左右箭头是必须的，不是锦上添花〔2026-09-14 用户报：在电脑上无法切换〕。
           vant 的 swipe 只认**手势滑动**，底部小圆点是指示器、不可点 ——
           手机上滑得动，桌面上要用鼠标按住拖拽，基本等于没法切。
           两张以下不显示箭头（没得切）。 -->
      <div v-if="mainImages.length" class="main-swipe-wrap">
        <van-swipe ref="mainSwipe" class="main-swipe" indicator-color="#0a8f6b" @change="onSwipeChange">
          <van-swipe-item v-for="img in mainImages" :key="img.url" @click="previewAt(mainImages, img)">
            <img class="main-img" :src="thumb(img.url)" :alt="img.fileName" />
          </van-swipe-item>
        </van-swipe>
        <template v-if="mainImages.length > 1">
          <button class="swipe-nav swipe-nav--prev" type="button" @click.stop="swipePrev">‹</button>
          <button class="swipe-nav swipe-nav--next" type="button" @click.stop="swipeNext">›</button>
          <span class="swipe-count">{{ swipeIndex + 1 }} / {{ mainImages.length }}</span>
        </template>
      </div>
      <!-- 主图类别理论上不该有 PDF，真传了也不能丢 —— 渲染成文件卡片，不塞进轮播 -->
      <div v-if="mainFiles.length" class="file-list">
        <div v-for="f in mainFiles" :key="f.url" class="file-card" @click="openFile(f)">
          <span class="file-icon">{{ (f.fileType || 'file').toUpperCase() }}</span>
          <span class="file-name">{{ f.fileName || f.url }}</span>
          <span class="file-open">{{ t('viewFile') }}</span>
        </div>
      </div>

      <div class="title">
        <div class="name">{{ report.productName }}</div>
        <div class="origin" v-if="report.originCountry">{{ t('origin') }}{{ colon }}{{ report.originCountry }}</div>
      </div>

      <section v-for="block in visibleBlocks" :key="block.slug" class="block">
        <h3 class="block-title" @click="toggle(block.slug)">
          <span>{{ t(block.slug) }}</span>
          <i class="arrow" :class="{ 'arrow--open': !collapsed[block.slug] }"></i>
        </h3>
        <!-- ⚠️ 折叠用 **v-if 不是 v-show**：`display:none` 里的 <img> 照样发请求，
             那样「企业资质/检测报告默认折叠」就只省了屏幕空间、没省流量，
             而这两块恰恰是证书图最多的。附件列表也没有折叠状态要保，重渲染无代价。
             ⚠️ 仍然**不做 opacity 动画** —— 约束 5：opacity 动画会把元素提升为
             GPU 合成层，同帧变文案时新旧两份同时可见。 -->
        <div v-if="!collapsed[block.slug]">
          <div v-for="row in block.rows" :key="row.key" class="row">
            <span class="label">{{ t(row.key) }}</span>
            <span class="value">{{ row.value }}</span>
          </div>

          <div v-if="block.attachments.length" class="attach-area">
            <!-- 公示报告是挂在「诚信信息」里的一项，得有自己的小标题；
                 企业资质/检测报告两块的标题就是区块名本身，不重复写 -->
            <div v-if="block.slug === 'integrity'" class="attach-label">{{ t('publicityReport') }}</div>
            <div class="attach-grid">
              <template v-for="a in block.attachments">
                <img
                  v-if="isImage(a)"
                  :key="a.url"
                  class="attach-img"
                  :src="thumb(a.url)"
                  :alt="a.fileName"
                  @click="previewAt(block.attachments, a)" />
                <div v-else :key="a.url" class="file-card" @click="openFile(a)">
                  <span class="file-icon">{{ (a.fileType || 'file').toUpperCase() }}</span>
                  <span class="file-name">{{ a.fileName || a.url }}</span>
                  <span class="file-open">{{ t('viewFile') }}</span>
                </div>
              </template>
            </div>
          </div>
        </div>
      </section>

      <div class="footer">
        <p>Copyright @2026 {{ t('platform') }}</p>
        <p>{{ t('techSupport') }}{{ colon }}{{ t('supporter') }}</p>
      </div>
    </template>
  </div>
</template>

<script>
import { ImagePreview } from 'vant'
import { scanCodePushReportV2 } from '@/common/api/appraisal'

// 只翻标签与固定文案，**值一律原样**（用户 2026-09-08 定：切英文时数据不变）。
// 做成页面内字典、不引 vue-i18n —— 全站只有这一个页面要双语，搭框架不划算。
//
// ⚠️ 键是 slug 不是中文。区块与字段行的 :key、折叠状态也都按这套 slug 存，
// 这样「语言」与「折叠状态」彻底解耦：若 :key 用翻译后的标题，切一次语言
// key 就全变、Vue 把每个 section 当新节点重建，已收起的区块会自己弹开。
const DICT = {
  zh: {
    platform: '中认鉴定商品溯源平台',
    techSupport: '技术支持',
    supporter: '北京中认检测技术服务有限公司',
    origin: '原产地',
    productLabel: '产品标签信息',
    productNo: '产品身份证码',
    productName: '产品名称',
    originCountry: '产地',
    productionDate: '生产日期',
    shelfLife: '保质期',
    chinaRegNo: '在华注册编号',
    productBatchNo: '产品批号',
    manufacturerName: '生产商名称',
    manufacturerAddress: '生产商地址',
    qualify: '企业资质信息',
    testReport: '检测报告信息',
    factoryAudit: '工厂审核',
    factoryName: '工厂名称',
    factoryAddress: '地址',
    integrity: '诚信信息',
    publicityOrg: '公示机构',
    publicityReport: '公示报告',
    viewFile: '点击查看',
    hintLoading: '加载中…',
    hintPending: '报告尚未生成',
    hintNotfound: '未找到该溯源码对应的报告',
    hintError: '网络异常，请稍后重试',
    hintEmpty: '暂无数据'
  },
  en: {
    // ⚠️ 平台名与公司名**在英文态也保留中文**〔2026-09-14 用户定：查得到官方英文名就用，
    // 查不到就保留中文〕。2026-09-14 联网查过：这家公司没有官方注册的英文名，
    // 网上能找到的只是机翻。专有名词译错了比不译更糟 —— 对外文件对不上。
    // 中认那边若给出正式英文名，改这两行即可。
    platform: '中认鉴定商品溯源平台',
    techSupport: 'Technical Support',
    // ⚠️ 公司法定名称**保留中文**：没有查到官方英文译名，自己音译或意译都可能与
    // 对外文件不一致。认证机构的名称属于不该擅自翻译的那一类。
    supporter: '北京中认检测技术服务有限公司',
    origin: 'Origin',
    productLabel: 'Product Label Information',
    productNo: 'Product ID',
    productName: 'Product Name',
    originCountry: 'Origin',
    productionDate: 'Production Date',
    shelfLife: 'Shelf Life',
    chinaRegNo: 'China Registration No.',
    productBatchNo: 'Batch No.',
    manufacturerName: 'Manufacturer',
    manufacturerAddress: 'Manufacturer Address',
    qualify: 'Business Qualification',
    testReport: 'Test Report',
    factoryAudit: 'Factory Audit',
    factoryName: 'Factory Name',
    factoryAddress: 'Address',
    integrity: 'Integrity Information',
    publicityReport: 'Public Disclosure Report',
    viewFile: 'View',
    // ⚠️ 「公示机构」的英文没有权威依据，是我的译法。原型里它的值是政府部门
    // （马来西亚农业与食品安全部）。中认那边若有官方说法，以他们的为准。
    publicityOrg: 'Disclosing Authority',
    hintLoading: 'Loading…',
    hintPending: 'Report not yet available',
    hintNotfound: 'No report found for this code',
    hintError: 'Network error, please try again',
    hintEmpty: 'No data'
  }
}

// 回退两级：en 缺键 → 退回中文；两边都缺 → **显示 slug 本身**。
// 显示 slug 是有意的：漏加一个键时页面上会出现 `productBatchNo` 这种明显不对的东西，
// 一眼看得见。渲染 undefined 或空串则会把漏键伪装成「这个字段没填」。
function translate(lang, key) {
  if (lang === 'en') return DICT.en[key] || DICT.zh[key] || key
  return DICT.zh[key] || key
}

export default {
  name: 'zrsy2',
  data() {
    return {
      loading: true,
      state: 'ok',     // ok | pending | notfound | error
      report: {},
      lang: 'zh',      // zh | en，只影响标签，不影响值
      // 按区块 slug 存折叠状态；**没有这个键 = 展开**。
      // 文本区块默认展开、两个纯附件区块默认折叠 —— 照原型（截图里它们就是收起的）。
      // ⚠️ 这两个键必须在这里**预先声明**：Vue 2 检测不到给对象新增的键，
      // 而且折叠态的意义在于首屏不去请求那些图（见模板里的 v-if）。
      collapsed: { qualify: true, testReport: true },
      // 主图轮播当前第几张 —— 只用于「2 / 3」那个计数，切换本身由 vant 自己管
      swipeIndex: 0
    }
  },
  computed: {
    // 中文用全角冒号紧贴，英文用半角加空格。标签本身进字典，标点不进——
    // 否则每个键都要在两套字典里各带一次标点，漏一个就是「Origin彭亨」。
    colon() {
      return this.lang === 'zh' ? '：' : ': '
    },
    hintText() {
      const map = {
        pending: 'hintPending',
        notfound: 'hintNotfound',
        error: 'hintError'
      }
      return this.t(map[this.state] || 'hintEmpty')
    },
    // 阶段 1 只做纯文本骨架，分区块按原型的顺序排。
    // 空值不渲染：整行去掉，区块内全空则整块去掉 —— 后端对没填的字段返回空串，
    // 若原样渲染会露出成片「标签 + 空白」。
    // 附件按 category 分组。1 商品主图 2 企业资质 3 检测报告 4 公示报告。
    // url 为空的丢掉（渲染不出东西），未知 category 也丢掉（前端没有它的位置）。
    attachmentsByCategory() {
      const g = { 1: [], 2: [], 3: [], 4: [] }
      ;(this.report.attachments || []).forEach((a) => {
        if (a && a.url && g[a.category]) g[a.category].push(a)
      })
      return g
    },
    visibleBlocks() {
      const r = this.report
      const att = this.attachmentsByCategory
      // 顺序照原型：产品标签信息 → 企业资质信息 → 检测报告信息 → 工厂审核 → 诚信信息。
      // 中间那两个是**纯附件区块**（原型里展开后就只有附件，没有文本行）。
      const blocks = [
        {
          slug: 'productLabel',
          rows: [
            ['productNo', r.productNo],
            ['productName', r.productName],
            ['originCountry', r.originCountry],
            ['productionDate', r.productionDate],
            ['shelfLife', r.shelfLife],
            ['chinaRegNo', r.chinaRegNo],
            ['productBatchNo', r.productBatchNo],
            ['manufacturerName', r.manufacturerName],
            ['manufacturerAddress', r.manufacturerAddress]
          ],
          attachCategory: 0
        },
        { slug: 'qualify', rows: [], attachCategory: 2 },
        { slug: 'testReport', rows: [], attachCategory: 3 },
        {
          slug: 'factoryAudit',
          rows: [
            ['factoryName', r.factoryName],
            ['factoryAddress', r.factoryAddress]
          ],
          attachCategory: 0
        },
        // 公示报告挂在诚信信息里，不单独成块 —— 原型就是这么排的
        { slug: 'integrity', rows: [['publicityOrg', r.publicityOrg]], attachCategory: 4 }
      ]
      return blocks
        .map((b) => ({
          slug: b.slug,
          attachCategory: b.attachCategory,
          attachments: b.attachCategory ? att[b.attachCategory] : [],
          rows: b.rows.filter((x) => x[1]).map((x) => ({ key: x[0], value: x[1] }))
        }))
        // ⚠️ 空值不渲染的判据是「文本行和附件都没有」——
        // 只看 rows 的话，「公示机构没填但传了公示报告」会让整块消失、附件没地方显示。
        .filter((b) => b.rows.length > 0 || b.attachments.length > 0)
    },
    // 顶部轮播只放商品主图里的**图片**。主图类别理论上不该有 PDF，
    // 真传了也不能丢，所以下面单独渲染成文件卡片。
    mainImages() {
      return this.attachmentsByCategory[1].filter((a) => this.isImage(a))
    },
    mainFiles() {
      return this.attachmentsByCategory[1].filter((a) => !this.isImage(a))
    }
  },
  created() {
    this.getReport()
  },
  methods: {
    t(key) {
      return translate(this.lang, key)
    },
    switchLang() {
      this.lang = this.lang === 'zh' ? 'en' : 'zh'
    },
    onSwipeChange(i) {
      this.swipeIndex = i
    },
    // ⚠️ 用 vant 实例的 prev/next，不自己算下标 —— 它内部要处理循环、动画、
    // 以及正在拖拽时的状态，自己 setIndex 会和手势打架。
    swipePrev() {
      const s = this.$refs.mainSwipe
      if (s) s.prev()
    },
    swipeNext() {
      const s = this.$refs.mainSwipe
      if (s) s.next()
    },
    // 按 file_type 分流。白名单判定，未知类型一律当文件处理 ——
    // 当图片渲染会得到一张破图，当文件至少点得开。
    isImage(a) {
      return a.fileType === 'jpg' || a.fileType === 'png'
    },
    // OSS 图片处理做缩略图。bucket 是公开读（后端返回的 url 是域名+对象名裸拼的、没有签名），
    // 所以前端拼参数即可，不用后端下发两个 url。
    // 满载时首屏能有十几 MB，压到 750px 宽是十分之一量级；点开预览再取原图。
    //
    // 【实验 2026-09-11】对 OSS 上的真实对象验过：加 resize 参数回 200 + image/png，
    // image/info 也正常 —— 这个 bucket 的图片处理是开通的。
    // ⚠️⚠️ **只能用在图片上**：同一个参数加到 PDF 上 OSS 直接回 **400**（实测），
    // 页面表现就是那个位置一片空白。所以调用点必须先过 isImage() ——
    // 模板里两处（主图轮播、区块内 <img>）都是这么做的，加第三处时别忘了。
    thumb(url) {
      if (!url || url.indexOf('?') >= 0) return url
      return url + '?x-oss-process=image/resize,w_750'
    },
    // ⚠️⚠️ 预览数组**只收图片**，起始下标用它在**图片数组**里的位置。
    // 同一类别下图片和 PDF 按 sort 混排，直接把混排下标传给 ImagePreview
    // 会「点第 2 张图跳到第 3 张」——中间夹了个 PDF 卡片。
    // 这里用 url 反查下标，从根上避开那个错位。
    previewAt(list, item) {
      const images = list.filter((a) => this.isImage(a)).map((a) => a.url)
      const i = images.indexOf(item.url)
      // ⚠️ `closeable` 必须开〔2026-09-14 用户报：点开大图退不回报告〕。
      // vant 默认只能**点图片本身**关闭，那个交互没有任何视觉提示 ——
      // 在微信里看报告的人会以为卡住了。closeOnPopstate 让安卓返回键也能退出。
      ImagePreview({
        images,
        startPosition: i < 0 ? 0 : i,
        closeable: true,
        closeOnPopstate: true
      })
    },
    openFile(a) {
      // PDF 不做内联渲染：pdf.js 几百 KB，后端转图要引 Go 渲染库，
      // 阿里云 IMM 要额外开通计费——都超出本期范围。
      // ⚠️ iOS 微信能直接预览，安卓微信通常是下载或弹「用其他应用打开」，这是微信的行为。
      window.open(a.url, '_blank')
    },
    toggle(slug) {
      // ⚠️ Vue 2 检测不到「给对象新增一个键」，直接 this.collapsed[slug] = x
      // 改得动数据但不会触发重渲染——点了没反应。必须走 $set。
      this.$set(this.collapsed, slug, !this.collapsed[slug])
    },
    getReport() {
      const { code: qrCode } = this.$route.params
      if (!qrCode) {
        this.state = 'notfound'
        this.loading = false
        return
      }
      // 与 V1 的两点不同：不调 addVisitNums（新版不展示浏览次数）；
      // 用 errorCode 分流而不是靠某个业务字段是否为空 —— 后端刻意让「未审核」与「码不存在」
      // 返回不同的 errorCode，正是为了让这里分得开。
      scanCodePushReportV2({ qrCode })
        .then((res) => {
          if (!res) {
            this.state = 'error'
          } else if (res.errorCode === 0) {
            this.report = res
            this.state = 'ok'
          } else if (res.errorCode === 404) {
            this.state = 'pending'
          } else {
            this.state = 'notfound'
          }
        })
        .catch(() => {
          this.state = 'error'
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.zrsy2-page {
  min-height: 100vh;
  background: #f5f6f8;
  padding: 12 * 2px;
  box-sizing: border-box;
  font-size: 14 * 2px;
  color: #3a3d44;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  // 用负 margin 抵消 .zrsy2-page 的 padding，让顶栏贴边全宽；
  // 其余内容照旧保留 padding，不必为顶栏单独包一层容器。
  margin: -12 * 2px -12 * 2px 10 * 2px;
  padding: 0 14 * 2px;
  height: 44 * 2px;
  background: #0a8f6b;
  color: #fff;
  .brand {
    font-size: 15 * 2px;
    font-weight: 600;
  }
  .lang {
    padding: 4 * 2px 10 * 2px;
    border: 1px solid rgba(255, 255, 255, 0.6);
    border-radius: 4 * 2px;
    background: transparent;
    color: #fff;
    font-size: 12 * 2px;
    line-height: 1;
  }
}
.hint {
  padding-top: 100 * 2px;
  text-align: center;
  color: #909399;
  font-size: 14 * 2px;
}
.title {
  padding: 12 * 2px 14 * 2px;
  background: #fff;
  border-radius: 8 * 2px;
  margin-bottom: 10 * 2px;
  .name {
    font-size: 17 * 2px;
    font-weight: 600;
    line-height: 24 * 2px;
  }
  .origin {
    margin-top: 6 * 2px;
    color: #909399;
    font-size: 13 * 2px;
  }
}
.block {
  background: #fff;
  border-radius: 8 * 2px;
  margin-bottom: 10 * 2px;
  overflow: hidden;
  .block-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;
    padding: 10 * 2px 14 * 2px;
    background: #0a8f6b;
    color: #fff;
    font-size: 14 * 2px;
    font-weight: 500;
    cursor: pointer;
    // 箭头用 border 拼的方块转 45°，旋转表达展开/收起。
    // 用 transform 不用 opacity —— 约束 5。
    .arrow {
      width: 7 * 2px;
      height: 7 * 2px;
      border-right: 2px solid #fff;
      border-bottom: 2px solid #fff;
      transform: translateY(-3px) rotate(45deg);
      &.arrow--open {
        transform: translateY(2px) rotate(-135deg);
      }
    }
  }
  .row {
    display: flex;
    border-bottom: 1px solid #f0f0f0;
    line-height: 20 * 2px;
    &:last-child {
      border-bottom: none;
    }
    // padding 落在 label / value 各自身上而不是 .row 上，
    // 这样标签列的浅绿底（照原型）能铺满整格、不在左右留白。
    .label {
      flex: 0 0 112 * 2px;
      padding: 10 * 2px 8 * 2px 10 * 2px 14 * 2px;
      background: #f2f8f5;
      color: #5c6169;
      word-break: break-word;   // 英文标签比中文长，允许折行
    }
    .value {
      flex: 1;
      padding: 10 * 2px 14 * 2px 10 * 2px 12 * 2px;
      word-break: break-all;
    }
  }
}
.main-swipe-wrap {
  position: relative;
  margin-bottom: 10 * 2px;
  // 箭头：半透明圆底 + 大号尖括号。压在图片两侧，不占版面高度。
  .swipe-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    width: 32 * 2px;
    height: 32 * 2px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.35);
    color: #fff;
    font-size: 24 * 2px;
    line-height: 1;
    // 尖括号字形本身偏下，往上顶一点才视觉居中
    padding-bottom: 4 * 2px;
    &--prev {
      left: 8 * 2px;
    }
    &--next {
      right: 8 * 2px;
    }
    &:active {
      background: rgba(0, 0, 0, 0.55);
    }
  }
  // 「2 / 3」计数，右下角
  .swipe-count {
    position: absolute;
    right: 10 * 2px;
    bottom: 10 * 2px;
    z-index: 2;
    padding: 2 * 2px 8 * 2px;
    border-radius: 10 * 2px;
    background: rgba(0, 0, 0, 0.35);
    color: #fff;
    font-size: 11 * 2px;
  }
}
.main-swipe {
  border-radius: 8 * 2px;
  overflow: hidden;
  background: #fff;
  .main-img {
    display: block;
    width: 100%;
    // ⚠️ 轮播要求每一屏等高，所以这里**必须固定高度** —— 竖排时可以「高度随图」，
    // 轮播不行：各屏高度不一会让容器在滑动时跳变。
    // 用 contain 而不是 cover：商品主图比例各不相同，cover 会把图裁掉一截。
    height: 320 * 2px;
    object-fit: contain;
    background: #fff;
  }
}
.file-list {
  margin-bottom: 10 * 2px;
}
.file-card {
  display: flex;
  align-items: center;
  padding: 12 * 2px 14 * 2px;
  margin-bottom: 8 * 2px;
  background: #fff;
  border-radius: 8 * 2px;
  .file-icon {
    flex: 0 0 auto;
    padding: 2 * 2px 6 * 2px;
    margin-right: 10 * 2px;
    background: #0a8f6b;
    color: #fff;
    border-radius: 3 * 2px;
    font-size: 11 * 2px;
  }
  .file-name {
    flex: 1;
    font-size: 13 * 2px;
    word-break: break-all;
  }
  .file-open {
    flex: 0 0 auto;
    margin-left: 10 * 2px;
    color: #0a8f6b;
    font-size: 12 * 2px;
  }
}
.attach-area {
  padding: 10 * 2px 14 * 2px 4 * 2px;
  .attach-label {
    margin-bottom: 8 * 2px;
    color: #5c6169;
    font-size: 13 * 2px;
  }
  .attach-grid {
    display: flex;
    flex-wrap: wrap;
    .attach-img {
      width: 100%;
      margin-bottom: 8 * 2px;
      border-radius: 4 * 2px;
      // 证书类是给人看内容的，别裁切；宽度铺满、高度随图
      object-fit: contain;
    }
    .file-card {
      width: 100%;
      padding: 10 * 2px 0;
      margin-bottom: 8 * 2px;
      background: transparent;
    }
  }
}
.footer {
  padding: 20 * 2px 0 30 * 2px;
  text-align: center;
  color: #b4b5be;
  font-size: 11 * 2px;
  line-height: 18 * 2px;
  p {
    margin: 0;
  }
}
</style>
